/**
 * Document Generation Mycelium Connector
 *
 * Bridges the proposal/invoice lifecycle with the mycelium event backbone,
 * enabling cross-domain automation:
 *
 * Key Flows:
 * 1. proposal.accepted → auto-generate invoice
 * 2. invoice.paid → update opportunity stage → award gamification XP
 * 3. proposal.sent → update sales performance metrics
 * 4. invoice.overdue → escalate notification channels
 * 5. invoice.paid → trigger Xero sync event
 *
 * Economy of Movement: Each event triggers only the minimum necessary
 * downstream actions, avoiding redundant processing.
 *
 * @module mycelium-document-generation
 * @since 2026-02-20
 */

import { myceliumEventBackbone, type MyceliumEvent } from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class DocumentGenerationConnector {
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;

    logger.info("📄 Document Generation connector initializing");

    // Listen for sales domain events that should trigger document generation
    // Priority 7: Below critical system events (8-10), above routine logging (1-5)
    // Ensures document lifecycle events are processed after core business logic
    myceliumEventBackbone.subscribe({
      name: "DocumentGenerationConnector",
      domains: ["sales", "accounting", "integration"],
      eventTypes: [
        // Proposal lifecycle events
        "proposal_created",
        "proposal_accepted",
        "proposal_rejected",
        "proposal_sent",
        // Invoice lifecycle events
        "invoice_created",
        "invoice_paid",
        "invoice_overdue",
        "invoice_payment_received",
        // External triggers
        "opportunity_closed_won",
      ],
      handler: this.handleEvent.bind(this),
      priority: 7,
    });

    this.initialized = true;
    logger.info("✅ Document Generation connector ready");
  }

  private async handleEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, payload } = event;

    try {
      switch (type) {
        case "proposal_accepted":
          await this.onProposalAccepted(event);
          break;

        case "proposal_sent":
          await this.onProposalSent(event);
          break;

        case "invoice_paid":
          await this.onInvoicePaid(event);
          break;

        case "invoice_overdue":
          await this.onInvoiceOverdue(event);
          break;

        case "invoice_payment_received":
          await this.onPaymentReceived(event);
          break;

        case "opportunity_closed_won":
          await this.onOpportunityClosedWon(event);
          break;

        default:
          logger.debug("📄 Unhandled document generation event", { type });
      }
    } catch (error) {
      logger.error("❌ Document Generation connector error", {
        eventType: type,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  // --------------------------------------------------------------------------
  // EVENT HANDLERS
  // --------------------------------------------------------------------------

  /**
   * When a proposal is accepted:
   * → Publish event for invoice auto-generation
   * → Notify sales team of win
   * → Award gamification XP
   */
  private async onProposalAccepted(event: MyceliumEvent): Promise<void> {
    const { userId, payload } = event;
    const proposalId = payload.documentId as string;
    const amount = payload.amount as number;

    logger.info("📄 Proposal accepted - triggering downstream actions", {
      proposalId,
      amount,
    });

    // Emit event for invoice auto-generation
    await getMyceliumTransport().publish({
      domain: "accounting",
      type: "invoice_auto_generate",
      userId,
      payload: {
        sourceProposalId: proposalId,
        clientId: payload.clientId,
        opportunityId: payload.opportunityId,
        amount,
        trigger: "proposal_accepted",
        source: "DocumentGenerationConnector",
      },
    });

    // Emit event for gamification
    await getMyceliumTransport().publish({
      domain: "gamification",
      type: "proposal_won",
      userId,
      payload: {
        proposalId,
        amount,
        action: "proposal_accepted",
        source: "DocumentGenerationConnector",
      },
    });
  }

  /**
   * When a proposal is sent:
   * → Update sales performance metrics
   * → Log activity for pipeline intelligence
   */
  private async onProposalSent(event: MyceliumEvent): Promise<void> {
    const { userId, payload } = event;

    logger.info("📄 Proposal sent - updating sales metrics", {
      proposalId: payload.documentId,
    });

    await getMyceliumTransport().publish({
      domain: "sales",
      type: "proposal_sent",
      userId,
      payload: {
        proposalId: payload.documentId,
        clientId: payload.clientId,
        opportunityId: payload.opportunityId,
        amount: payload.amount,
        source: "DocumentGenerationConnector",
      },
    });
  }

  /**
   * When an invoice is fully paid:
   * → Update opportunity to closed-won
   * → Trigger commission calculation
   * → Award gamification XP
   * → Trigger Xero sync
   */
  private async onInvoicePaid(event: MyceliumEvent): Promise<void> {
    const { userId, payload } = event;
    const invoiceId = payload.documentId as string;
    const amount = payload.amount as number;

    logger.info("💰 Invoice paid - triggering downstream actions", {
      invoiceId,
      amount,
    });

    // Trigger commission calculation
    await getMyceliumTransport().publish({
      domain: "sales",
      type: "revenue_collected",
      userId,
      payload: {
        invoiceId,
        amount,
        clientId: payload.clientId,
        opportunityId: payload.opportunityId,
        source: "DocumentGenerationConnector",
      },
    });

    // Trigger Xero sync
    await getMyceliumTransport().publish({
      domain: "integration",
      type: "xero_sync_required",
      userId,
      payload: {
        invoiceId,
        amount,
        action: "invoice_paid",
        source: "DocumentGenerationConnector",
      },
    });

    // Award XP for payment collection
    await getMyceliumTransport().publish({
      domain: "gamification",
      type: "payment_collected",
      userId,
      payload: {
        invoiceId,
        amount,
        action: "invoice_paid",
        source: "DocumentGenerationConnector",
      },
    });
  }

  /**
   * When an invoice becomes overdue:
   * → Escalate notification channels
   * → Flag in sales pipeline
   */
  private async onInvoiceOverdue(event: MyceliumEvent): Promise<void> {
    const { userId, payload } = event;

    logger.warn("⚠️ Invoice overdue - escalating notifications", {
      invoiceId: payload.documentId,
    });

    await getMyceliumTransport().publish({
      domain: "notification",
      type: "invoice_overdue_escalation",
      userId,
      payload: {
        invoiceId: payload.documentId,
        amount: payload.amount,
        clientId: payload.clientId,
        daysOverdue: payload.daysOverdue,
        source: "DocumentGenerationConnector",
      },
    });
  }

  /**
   * When a partial payment is received:
   * → Log payment activity
   * → Update pipeline intelligence
   */
  private async onPaymentReceived(event: MyceliumEvent): Promise<void> {
    const { userId, payload } = event;

    logger.info("💳 Partial payment received", {
      invoiceId: payload.documentId,
      amount: payload.paymentAmount,
    });

    await getMyceliumTransport().publish({
      domain: "sales",
      type: "partial_payment_received",
      userId,
      payload: {
        invoiceId: payload.documentId,
        paymentAmount: payload.paymentAmount,
        remainingBalance: payload.remainingBalance,
        clientId: payload.clientId,
        source: "DocumentGenerationConnector",
      },
    });
  }

  /**
   * When an opportunity is closed-won:
   * → Suggest auto-generating a proposal or invoice
   */
  private async onOpportunityClosedWon(event: MyceliumEvent): Promise<void> {
    const { userId, payload } = event;

    logger.info("🎯 Opportunity closed-won - suggesting document generation", {
      opportunityId: payload.opportunityId,
    });

    await getMyceliumTransport().publish({
      domain: "notification",
      type: "suggest_invoice_generation",
      userId,
      payload: {
        opportunityId: payload.opportunityId,
        clientId: payload.clientId,
        dealValue: payload.value,
        message: "Opportunity closed! Generate an invoice to collect payment.",
        source: "DocumentGenerationConnector",
      },
    });
  }

  // --------------------------------------------------------------------------
  // LIFECYCLE
  // --------------------------------------------------------------------------

  async dispose(): Promise<void> {
    if (!this.initialized) return;

    myceliumEventBackbone.unsubscribe("DocumentGenerationConnector");
    this.initialized = false;
    logger.info("🛑 Document Generation connector disposed");
  }
}

/**
 * Singleton connector instance
 */
export const documentGenerationConnector = new DocumentGenerationConnector();
